/**
 * React component that is a wrapper around react-quill that sets up a text editor to our preferences
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import ReactQuill from 'react-quill'
import onClickOutside from 'react-onclickoutside'
import isFunction from 'lodash/isFunction'
import { isHotkey } from 'client/shared/js/utils/isHotkey'
import { isEmpty, isSlateObj } from './utils/editorHelper'
import './modules'
import './formats'
import './RichTextEditor.scss'

const FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet',
  'link', 'emoji'
]

const editorThemeTypes = {
  'toolbar': {
    theme: 'snow',
    formats: FORMATS,
    toolbar: [
      [{ 'header': 1 }, { 'header': 2 }, 'bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, 'link', 'emoji']
    ]
  },
  'tooltip': {
    theme: 'bubble',
    formats: FORMATS,
    toolbar: [
      [{ 'header': 1 }, { 'header': 2 }, 'bold', 'italic', 'underline', 'strike'],
      ['link', 'emoji']
    ]
  }
}

class RichTextEditor extends Component {
  static propTypes = {
    className: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onChange: PropTypes.func,
    onChangeSelection: PropTypes.func,
    onEditorFocus: PropTypes.func,
    onEditorBlur: PropTypes.func,
    registerHotkeys: PropTypes.object,
    placeholder: PropTypes.string,
    footnote: PropTypes.string,
    editable: PropTypes.bool,
    type: PropTypes.oneOf(['toolbar', 'tooltip']),
    autofocus: PropTypes.bool,
    historyOnMount: PropTypes.bool, // Enable or disable history even with mounted value

    // Provided by react-onclickoutside
    onClickOutside: PropTypes.func,
    disableOnClickOutside: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    enableOnClickOutside: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
  }

  static defaultProps = {
    className: '',
    onChange: () => false,
    onChangeSelection: () => false,
    onEditorFocus: () => false,
    onEditorBlur: () => false,
    registerHotkeys: {},
    placeholder: '',
    editable: true,
    type: null,
    autofocus: false,
    historyOnMount: false
  }

  quillRef = React.createRef()

  componentDidMount = () => {
    const ref = this.quillRef?.current

    if (this.props.autofocus && ref) {
      ref.focus()
    }
  }

  getEditor = () => {
    return this.quillRef?.current?.getEditor()
  }

  /**
   * Toggles the onClickOutside event handler
   * By default the onClickOutside listener is disbabled unless the specified props are set.
   * The editor has to be both editable and provided a onClickOutside function to enable onClickOutside.
   * @param {Boolean} isEnabled The boolean that indidcates whether onClickOutside should be enabled/disabled
   */
  toggleClickOutsideListener = (isEnabled) => {
    if (this.props.editable && isEnabled && this.props.onClickOutside) {
      this.props.enableOnClickOutside()
    } else {
      this.props.disableOnClickOutside()
    }
  }

  handleChange = (content, delta, source, editor) => {
    this.props.onChange(editor)
  }

  handleChangeSelection = (range, source, editor) => {
    this.props.onChangeSelection(range, source, editor)
  }

  handleFocus = (range, source, editor) => {
    if (this.props.editable) {
      this.props.onEditorFocus(editor)
      this.toggleClickOutsideListener(true)
    }
  }

  handleBlur = (previousRange, source, editor) => {
    this.props.onEditorBlur(editor)
  }

  /**
   * We have to leverage react-onclickoutside because of Quill issues with 'blur' events + button clicks
   *  See: https://github.com/quilljs/quill/issues/2186
   */
  handleClickOutside = () => {
    if (this.props.onClickOutside) {
      this.props.onClickOutside(this.getEditor())
    }

    this.toggleClickOutsideListener(false)
  }

  handleKeyDown = (evt) => {
    const { registerHotkeys } = this.props

    // Registers all of our hotkeys and performs the associated action
    Object.keys(registerHotkeys).forEach((command) => {
      const action = registerHotkeys[command]
      const isCommandHotkey = isHotkey(command)

      if (isCommandHotkey(evt) && isFunction(action)) {
        evt.preventDefault()
        const editor = this.getEditor()
        action.call(this, editor)
      }
    })
  }

  getThemeSettings = () => {
    const themeType = editorThemeTypes[this.props.type] || null

    if (!themeType || !this.props.editable) {
      return { theme: null }
    }

    return {
      theme: themeType.theme,
      modules: {
        history: {
          userOnly: !this.props.historyOnMount
        },
        toolbar: {
          container: themeType.toolbar,
          handlers: {
            'emoji': () => {}
          }
        },
        'emoji-toolbar': true,
        'magicUrl': true
        // Currently shortname does not work. We can read more here: https://github.com/contentco/quill-emoji/issues/88
        // 'emoji-shortname': true
      },
      formats: themeType.formats
    }
  }

  renderFootnote = () => {
    return (this.props.footnote) ? (
      <p className='description'>{this.props.footnote}</p>
    ) : null
  }

  render () {
    const richTextContainerClass = cx('rich-text-editor-container', this.props.className, {
      'empty-editor': isEmpty(this.props.value)
    })
    const themeSettings = this.getThemeSettings()

    // Only add value prop if we want it to be a controlled value
    const valueProp = (this.props.value) ? {
      value: (!isSlateObj(this.props.value)) ? this.props.value : {}
    } : {}

    const quillProps = {
      ref: this.quillRef,
      defaultValue: this.props.defaultValue,
      placeholder: this.props.placeholder,
      onChange: this.handleChange,
      onChangeSelection: this.handleChangeSelection,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onKeyDown: this.handleKeyDown,
      readOnly: !this.props.editable,
      ...themeSettings,
      ...valueProp
    }

    return (
      <div className={richTextContainerClass}>
        <ReactQuill {...quillProps} />
        {this.renderFootnote()}
      </div>
    )
  }
}

const WrappedRichTextEditor = onClickOutside(RichTextEditor)

// We wnant to disableOnClickOutside by default since the existence of `onOutsideClick`
// will determine trigger whether or not to enable the click outside listener
export default React.forwardRef((props, ref) => (
  <WrappedRichTextEditor
    ref={ref}
    disableOnClickOutside
    {...props} />
))
